import { Button, Result, Space, Typography } from 'antd';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

const getErrorMessage = (error) => {
	if (!error) return 'Unknown error';
	if (typeof error === 'string') return error;
	if (error instanceof Error) return error.message;
	if (typeof error === 'object' && 'message' in error) return String(error.message);
	return 'Unexpected application error';
};

const getRouteErrorDescription = (error) => {
	if (!error) return 'The requested page failed to load.';
	if (typeof error.data === 'string') return error.data;
	if (error.data && typeof error.data === 'object' && 'message' in error.data) {
		return String(error.data.message);
	}
	return 'The requested page failed to load.';
};

export default function DefaultRouteErrorBoundary() {
	const error = useRouteError();
	const navigate = useNavigate();
	const isRouteResponseError = isRouteErrorResponse(error);
	const isDevelopment = import.meta.env.DEV;

	const title = isRouteResponseError ? `${error.status} ${error.statusText}` : 'Something went wrong';
	const description = isRouteResponseError
		? getRouteErrorDescription(error)
		: 'An unexpected error occurred while rendering this page.';
	const errorMessage = getErrorMessage(error);

	return (
		<div
			style={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 24,
			}}
		>
			<Result
				status="error"
				title={title}
				subTitle={description}
				extra={
					<Space wrap>
						<Button type="primary" onClick={() => window.location.reload()}>
							Reload Page
						</Button>
						<Button onClick={() => navigate('/')}>Go Home</Button>
						<Button onClick={() => navigate(-1)}>Go Back</Button>
					</Space>
				}
			>
				<Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
					{errorMessage}
				</Typography.Paragraph>
				{isDevelopment && error?.stack ? (
					<Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
						<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error.stack}</pre>
					</Typography.Paragraph>
				) : null}
			</Result>
		</div>
	);
}
